import config from '@config'
import usePageMetadata from '@hooks/usePageMetadata'
import { mobileCSS } from '@measures/responsive'
import { SocialProps } from '@widgets/SocialShare/types'
import GamificationButton from '@components/Buttons/GamificationButton'
import { FunctionComponent } from 'react'
import styled, { css } from 'styled-components'
import EndOfArticleContext from '@contexts/endOfArticle'
import { stripHtml } from '@hooks/useTracking/utils'
import useIsInRecipeContent from '@hooks/useIsInRecipeContent'
import { printCSS } from '@utils/style'
import Feedback from '@components/Social/Feedback'
import PlusLinkShareButton from '@components/PlusLinkShare/Button'
import GoogleNews from '@components/Social/GoogleNews'
import useABTestValue from '@hooks/useABTestValue'
import useBlickPlusSkeleton from '@hooks/useBlickPlusSkeleton'
import ShareCTAButton from './ShareCTAButton'

const {
  backend: { baseUrl, rootUrl },
  socialembeds: { blickplusTitleAddition },
} = config

const StyledSocial = styled.div<{ isInRecipe: boolean; endOfArticle: boolean }>`
  ${({
    theme: {
      color: {
        tertiary: { grey300 },
      },
      spacing: { spacing8, spacing16, spacing20 },
    },
    isInRecipe,
    endOfArticle,
  }) => css`
    display: flex;
    justify-content: space-between;
    flex: 1;

    ${printCSS(css`
      display: none;
    `)}

    ${mobileCSS(css`
      align-self: flex-start;
    `)}

    margin: ${isInRecipe && !endOfArticle
      ? `${spacing16} ${spacing8} ${spacing16} 0`
      : `${spacing8} ${spacing8} ${spacing8} 0`};

    ${endOfArticle &&
    css`
      margin-bottom: 0;
      margin-right: 0;
      padding-bottom: ${spacing20};
      border-bottom: 1px solid ${grey300};
    `}
  `}
`

const SocialShareCTAWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    align-items: center;
    display: flex;
    gap: ${spacing8};
  `}
`

const StyledSocialWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    display: flex;
    align-items: center;
    flex-direction: row;
    gap: ${spacing8};
  `}
`

const SocialButtonsContainer = styled.div`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${spacing8};
  `}
`

const Social: FunctionComponent<SocialProps> = ({ endOfArticle }) => {
  const { id, url, teaser, title, catchword } = usePageMetadata()

  const isInRecipe = useIsInRecipeContent()
  // TODO Cleanup after go live: remove the AB test check and use the skeleton value only, or remove isPlusSharingLive completely.
  const { isPlusSharingLive: isPlusSharingLiveSkeleton } =
    useBlickPlusSkeleton()
  const isPlusSharingABTestEnabled =
    useABTestValue('blickPlusLinkSharing') === 'active'
  const isPlusSharingLive =
    isPlusSharingLiveSkeleton || isPlusSharingABTestEnabled

  if (!url || !id) {
    return null
  }

  const blickplusTitleAdditionForUrl = encodeURIComponent(
    ` ${blickplusTitleAddition}`
  )

  const sharingTitle = teaser?.isPlus
    ? `${stripHtml(title)}${blickplusTitleAdditionForUrl}`
    : stripHtml(title)
  const sharingCatchword = catchword ? stripHtml(catchword) : ''

  const shareUrl = `${baseUrl}${rootUrl}${url.slice(1)}`
  const fullShareTitleAndCatchword = `${sharingCatchword}%20${sharingTitle}`

  return (
    <EndOfArticleContext.Provider value={endOfArticle}>
      <StyledSocial isInRecipe={isInRecipe} endOfArticle={endOfArticle}>
        <StyledSocialWrapper>
          <SocialButtonsContainer>
            <SocialShareCTAWrapper>
              <ShareCTAButton
                title={fullShareTitleAndCatchword}
                articleId={id}
              />
            </SocialShareCTAWrapper>
            {isPlusSharingLive && !endOfArticle && <PlusLinkShareButton />}
          </SocialButtonsContainer>
          {endOfArticle && <GoogleNews />}
        </StyledSocialWrapper>
        <GamificationButton />
      </StyledSocial>
      {endOfArticle ? (
        <Feedback articleUrl={shareUrl} articleTitle={title ?? ''} />
      ) : null}
    </EndOfArticleContext.Provider>
  )
}

export default Social
